import { useMemo, useState } from 'react';
import type { BoardroomCatalogueEntry, BoardroomDeliverable } from '../../../types';
import { fillLine } from './DeliverableTile';
import MadeByTag from './MadeByTag';
import { provenance } from './provenance';

/**
 * Everything this room could show, grouped by what it is about.
 *
 * PICKING WHAT TO LOOK AT IS BROWSING, not an interview question. The opening
 * conversation asks what the room should WORK toward; which of those tables is
 * on screen at any moment is a decision made later, repeatedly, by whoever is
 * reading — so it lives here behind one button rather than in a conversation
 * nobody wants to have twice.
 *
 * The Dashboard is what is pinned; this is everything else. A table is
 * pinned or it is here - there is no third place.
 *
 * Every table the room has is one its digs work on; pinning only decides what
 * is on the Dashboard. A dig aimed at one table is the Research window's job.
 */
export default function DeliverableBrowser({
  deliverables,
  catalogue,
  onPin,
  onAdd,
  onDelete,
  onRestore,
  onClose,
  embedded = false,
}: {
  deliverables: BoardroomDeliverable[];
  catalogue: BoardroomCatalogueEntry[];
  onPin: (d: BoardroomDeliverable) => void;
  /** A table the room has never had: choose it. */
  onAdd: (kind: string, pinned: boolean) => void;
  /** Turn the seats onto it (or off it). */
  /** Remove a card the catalogue does not list, for good. */
  onDelete?: (d: BoardroomDeliverable) => void;
  /** Bring a card taken off the shelf back onto it. */
  onRestore?: (d: BoardroomDeliverable) => void;
  onClose: () => void;
  /** Inside a panel that has its own title and close: no header here. */
  embedded?: boolean;
}) {
  // THE PLAIN TABLE, NOT A CUT OF IT. A card is a kind plus a subject, so a
  // room with "Competitors × oversized" has two cards of kind `competitors` —
  // and a map keyed on kind alone handed this row whichever came last. Pinning
  // "Competitors" would then toggle the cut, and the table itself never moved.
  const byKind = useMemo(
    () => new Map(deliverables.filter(d => !d.subject).map(d => [d.kind, d])),
    [deliverables],
  );
  const groups = useMemo(() => {
    const out: [string, BoardroomCatalogueEntry[]][] = [];
    for (const c of catalogue) {
      // Catalogue order IS the grouping order — the room's first questions
      // (who is here) before its last (what to do about it).
      const found = out.find(([name]) => name === c.group);
      if (found) found[1].push(c);
      else out.push([c.group, [c]]);
    }
    return out;
  }, [catalogue]);

  // EVERY CARD THE CATALOGUE BELOW DOES NOT LIST: what the chat built, and
  // the cards made for one thing (a pain's page, one company's searches, a cut
  // of a table). Closing or taking one off the shelf leaves it here, so this
  // is where it comes back from, or gets deleted. Only listing the chat's
  // tables made the shelf's × lose a noticing someone had taken.
  const listed = useMemo(() => new Set(catalogue.map(c => c.kind)), [catalogue]);
  const own = useMemo(
    () =>
      deliverables
        .filter(d => !listed.has(d.kind) || !!d.subject)
        .sort((a, b) => a.position - b.position),
    [deliverables, listed],
  );
  const titleOf = (kind: string) => catalogue.find(c => c.kind === kind)?.title ?? kind;
  // FIND ONE BY TYPING. Every word must appear in its name, what it answers or
  // what sort of card it is - so "price" finds the price map, and "tools
  // cost" the report the chat built about what tools cost.
  const [query, setQuery] = useState('');
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  const hit = (...texts: (string | null | undefined)[]) => {
    if (!words.length) return true;
    const hay = texts.filter(Boolean).join(' ').toLowerCase();
    return words.every(w => hay.includes(w));
  };
  const built = own.filter(
    d => (d.kind === 'report' || d.kind === 'list') && hit(d.title, d.answers, provenance(d, titleOf)),
  );
  const added = own.filter(
    d => d.kind !== 'report' && d.kind !== 'list' && hit(d.title, d.answers, provenance(d, titleOf)),
  );
  const shownGroups = groups
    .map(([group, entries]) => [
      group,
      entries.filter(c => hit(byKind.get(c.kind)?.title, c.title, c.answers, group)),
    ] as [string, BoardroomCatalogueEntry[]])
    .filter(([, entries]) => entries.length > 0);
  const nothing = !built.length && !added.length && !shownGroups.length;
  // SHORT CARDS IN A GRID, as many across as fit: full-width rows spent a whole
  // line of a wide panel on a name and two buttons.
  const section = (heading: string, cards: BoardroomDeliverable[]) => (
    <div>
      <div className="mb-1 text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
        {heading}
      </div>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] gap-2">
        {cards.map(d => (
          <div
            key={d.id}
            className="flex flex-col gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-2 dark:border-gray-700 dark:bg-gray-800"
            data-testid={`browser-built-${d.id}`}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate text-xs font-medium text-gray-900 dark:text-gray-100" title={d.title}>
                  {d.title}
                </span>
                {d.pinned && d.status !== 'dropped' && (
                  <span className="shrink-0 rounded bg-indigo-50 px-1 text-[11px] text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200">
                    pinned
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {/* What it is, who made it and when - then what it answers. */}
                {provenance(d, titleOf)}
                {d.answers ? <div className="text-gray-400">{d.answers}</div> : null}
              </div>
            </div>
            <div className="mt-auto flex flex-wrap items-center gap-1.5">
              {d.status === 'dropped' ? (
                onRestore && (
                  <button
                    type="button"
                    onClick={() => onRestore(d)}
                    className="whitespace-nowrap rounded border border-gray-300 dark:border-gray-600 px-1.5 py-0.5 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                    data-testid={`browser-restore-${d.id}`}
                  >
                    Pin to Dashboard
                  </button>
                )
              ) : (
                <button
                  type="button"
                  onClick={() => onPin(d)}
                  className="whitespace-nowrap rounded border border-gray-300 dark:border-gray-600 px-1.5 py-0.5 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  {d.pinned ? 'Unpin' : 'Pin to Dashboard'}
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  onClick={() => onDelete(d)}
                  className="whitespace-nowrap rounded px-1.5 py-0.5 text-xs text-red-600 hover:underline dark:text-red-400"
                >
                  Delete
                </button>
              )}
              <span className="ml-auto">
                <MadeByTag deliverable={d} title={provenance(d, titleOf)} testId={`browser-made-by-${d.id}`} />
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex h-full flex-col">
      {!embedded && (
      <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 px-3 py-2">
        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
          What this room can show
        </h3>
        <span className="text-[11px] text-gray-400 dark:text-gray-500">
          pinned = on the Dashboard · actively fill = the room spends on it
        </span>
        <button
          type="button"
          onClick={onClose}
          className="ml-auto rounded px-2 py-0.5 text-xs text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          Done
        </button>
      </div>
      )}

      <div className="px-3 pt-2">
        <input
          type="search"
          value={query}
          onChange={e => setQuery(e.target.value)}
          autoFocus={embedded}
          placeholder="Find a table, report or page…"
          aria-label="Find something to add"
          className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          data-testid="browser-filter"
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2 space-y-4">
        {nothing && (
          <p className="text-sm text-gray-500 dark:text-gray-400">Nothing here matches “{query.trim()}”.</p>
        )}
        {built.length > 0 && section('Views built in the chat', built)}
        {added.length > 0 && section('Views you kept', added)}
        {shownGroups.map(([group, entries]) => (
          <div key={group}>
            <div className="mb-1 text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
              {group}
            </div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] gap-2">
              {entries.map((c: BoardroomCatalogueEntry) => {
                const have = byKind.get(c.kind);
                const dropped = have?.status === 'dropped';
                return (
                  <div
                    key={c.kind}
                    className="flex flex-col gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-2 dark:border-gray-700 dark:bg-gray-800"
                    data-testid={`browser-row-${c.kind}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-xs font-medium text-gray-900 dark:text-gray-100">
                          {have?.title || c.title}
                        </span>
                        {have && !dropped && have.pinned && (
                          <span className="shrink-0 rounded bg-indigo-50 px-1 text-[11px] text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200">
                            pinned
                          </span>
                        )}
                      </div>
                      <div className="line-clamp-3 text-xs text-gray-500 dark:text-gray-400" title={c.answers}>
                        {c.answers}
                      </div>
                      {have && !dropped && (
                        <div className="text-xs text-gray-400 dark:text-gray-500">
                          {fillLine(have)}
                        </div>
                      )}
                    </div>
                    <div className="mt-auto flex flex-wrap items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() =>
                          have && !dropped ? onPin(have) : onAdd(c.kind, true)
                        }
                        title={
                          have && !dropped && have.pinned
                            ? 'Unpin it from the Dashboard. It stays in All views.'
                            : 'Pin it to the Dashboard.'
                        }
                        className="whitespace-nowrap rounded border border-gray-300 dark:border-gray-600 px-1.5 py-0.5 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        {/* THE SCREEN, NOT THE WORK — and a VERB, so it reads
                            as something you are about to do rather than a
                            label for where the table currently is. "On the
                            board" sat next to a chip saying "on the board" and
                            had to be read twice to tell them apart. */}
                        {have && !dropped && have.pinned
                          ? 'Unpin'
                          : 'Pin to Dashboard'}
                      </button>
                      {/* Every table the catalogue lists is built in. */}
                      <span className="ml-auto">
                        <MadeByTag
                          deliverable={{ kind: c.kind, subject: '', created_by: 'default' }}
                          testId={`browser-made-by-${c.kind}`}
                        />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
